import { useCallback, useEffect, useRef, useState } from "react";
import {
    Building2,
    Check,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    Info,
    Mail,
    MapPin,
    Phone,
    PhoneOff,
    Plus,
    RotateCcw,
    Search,
    Trash2,
    User,
    X,
} from "lucide-react";
import {
    addCallNote,
    addCallTask,
    dropCallTask,
    getCallList,
    logCallAttempt,
    markCalled,
    reopenCallTask,
    CallListResponse,
    CallOutcome,
    CallProfile,
    CallTask,
    CallTaskStatus,
    DROP_REASON_LABEL,
    OUTCOME_LABEL,
} from "interfaces/commercialCalls";
import { searchEstablishmentsAdmin } from "interfaces/establishments";
import { PREVIEW_LEAD_DAYS } from "interfaces/weeklyMail";
import BOCommercialNav from "./BOCommercialNav";

const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
const DAY_MS = 86_400_000;

/**
 * How long a task lives — `CALL_TASK_TTL_DAYS` server-side, and the same number as the brief's
 * lead time on purpose: a task exists for exactly the window between its brief being prepared
 * and the client reading it. Spent from one place so the two cannot drift apart in the copy.
 */
const TTL_DAYS = PREVIEW_LEAD_DAYS;

const OUTCOMES: CallOutcome[] = ["reached", "interested", "callback", "not_interested", "wrong_number"];

const OUTCOME_TONE: Record<CallOutcome, string> = {
    reached: "text-info bg-info-subtle border-info/20",
    interested: "text-success bg-success-subtle border-success/20",
    callback: "text-warning bg-warning-subtle border-warning/20",
    not_interested: "text-ink-muted bg-surface-sunken border-border",
    wrong_number: "text-danger bg-danger-subtle border-danger/20",
    no_answer: "text-ink-muted bg-surface-sunken border-border",
};

// ─── small pieces ─────────────────────────────────────────────────────────────

function formatDate(value: string | null): string {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function formatDateTime(value: string | null): string {
    if (!value) return "—";
    return new Date(value).toLocaleString("fr-FR", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function daysLeft(expiresAt: string): number {
    return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / DAY_MS);
}

/**
 * Moves one row between the three counters. Written as a transition rather than an increment
 * because rows go both ways — reopening a closed call has to give its count back, and a
 * one-directional `+1` silently inflates the tally over a session.
 */
function shiftStats(
    stats: CallListResponse["stats"],
    from: CallTaskStatus | undefined,
    to: CallTaskStatus
): CallListResponse["stats"] {
    const next = { ...stats };
    if (from && from !== to) next[from] = Math.max(0, next[from] - 1);
    if (from !== to) next[to] += 1;
    return next;
}

/** The client this call is for. Every row carries one, manual ones included. */
function ProfileTag({ name }: { name: string }) {
    if (!name) return null;
    return (
        <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-surface-sunken px-2.5 py-1 text-xs font-medium text-ink-muted">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            <span className="truncate">{name}</span>
        </span>
    );
}

function OutcomeBadge({ outcome }: { outcome: CallOutcome | null }) {
    if (!outcome) return <span className="text-xs text-ink-faint">—</span>;
    return (
        <span
            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${OUTCOME_TONE[outcome]}`}
        >
            {OUTCOME_LABEL[outcome]}
        </span>
    );
}

/** A phone number or an address is only useful if it can be dialled or opened. */
function ContactCell({ task }: { task: CallTask }) {
    const [copied, setCopied] = useState<string | null>(null);

    const copy = (value: string) => {
        navigator.clipboard?.writeText(value).then(
            () => {
                setCopied(value);
                setTimeout(() => setCopied(null), 1500);
            },
            () => {}
        );
    };

    if (!task.phone && !task.email) {
        return <span className="text-xs text-ink-faint">Aucun contact</span>;
    }

    return (
        <div className="space-y-1">
            {task.phone && (
                <div className="flex items-center gap-1.5">
                    <a
                        href={`tel:${task.phone.replace(/\s/g, "")}`}
                        className={`inline-flex items-center gap-1.5 rounded font-mono text-sm text-ink hover:text-accent ${FOCUS_RING}`}
                    >
                        <Phone className="h-3.5 w-3.5 text-ink-faint" />
                        {task.phone}
                    </a>
                    <button
                        onClick={() => copy(task.phone!)}
                        title="Copier le numéro"
                        className={`rounded p-0.5 text-ink-faint transition-colors hover:text-ink ${FOCUS_RING}`}
                    >
                        {copied === task.phone ? <Check className="h-3 w-3 text-success" /> : <span className="text-xs">⧉</span>}
                    </button>
                </div>
            )}
            {task.email && (
                <a
                    href={`mailto:${task.email}`}
                    className={`flex items-center gap-1.5 rounded text-xs text-ink-muted hover:text-accent ${FOCUS_RING}`}
                >
                    <Mail className="h-3 w-3 text-ink-faint" />
                    <span className="truncate">{task.email}</span>
                </a>
            )}
        </div>
    );
}

function NoteList({ notes }: { notes: CallTask["notes"] }) {
    if (notes.length === 0) {
        return <p className="text-xs text-ink-faint">Aucune note pour l'instant.</p>;
    }
    return (
        <ul className="space-y-2">
            {notes.map((note) => (
                <li key={note._id} className="rounded-lg border border-border bg-canvas px-3 py-2">
                    <p className="whitespace-pre-wrap text-sm text-ink">{note.text}</p>
                    <p className="mt-1 text-xs text-ink-faint">
                        {note.authorName || "Admin"} · {formatDateTime(note.createdAt)}
                    </p>
                </li>
            ))}
        </ul>
    );
}

// ─── manual add ───────────────────────────────────────────────────────────────

/**
 * Adding a call by hand. The profile is asked for, not inferred: the tag on the row is what
 * tells the caller which client the call is for, and a task without one would be unreadable
 * next to the ones a brief created.
 */
function AddCallPanel({
    profiles,
    onAdded,
    onClose,
}: {
    profiles: CallProfile[];
    onAdded: (task: CallTask) => void;
    onClose: () => void;
}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState<any | null>(null);
    const [profileId, setProfileId] = useState(profiles.find((p) => p.active)?._id ?? profiles[0]?._id ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // The panel can be opened before the first load has answered, leaving the select empty
    // with no way back — adopt the profiles as soon as they arrive.
    useEffect(() => {
        if (!profileId && profiles.length > 0) {
            setProfileId(profiles.find((p) => p.active)?._id ?? profiles[0]._id);
        }
    }, [profiles, profileId]);

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const data = await searchEstablishmentsAdmin({ q: query, limit: 8 });
                setResults(data.establishments as any[]);
            } catch {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 280);
        return () => clearTimeout(timer);
    }, [query]);

    const submit = async () => {
        if (!selected || !profileId) return;
        setSaving(true);
        setError(null);
        try {
            const task = await addCallTask(selected._id, profileId);
            onAdded(task);
            onClose();
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Impossible d'ajouter cet établissement");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border-t border-border bg-canvas px-6 py-5">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-sm font-semibold text-ink">Ajouter un appel</h3>
                    <p className="mt-0.5 text-xs text-ink-muted">
                        Il tombera automatiquement de la liste au bout de {TTL_DAYS} jours, comme les autres.
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className={`rounded-md p-1 text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink ${FOCUS_RING}`}
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
                {/* establishment */}
                <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Établissement</label>
                    {selected ? (
                        <div className="mt-1.5 flex items-center justify-between gap-2 rounded-lg border border-border bg-surface px-3 py-2">
                            <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-ink">{selected.name}</p>
                                <p className="truncate text-xs text-ink-muted">
                                    {[selected.address?.city, selected.address?.postcode].filter(Boolean).join(" · ") || "Adresse inconnue"}
                                </p>
                            </div>
                            <button
                                onClick={() => setSelected(null)}
                                className={`shrink-0 rounded p-1 text-ink-faint transition-colors hover:text-danger ${FOCUS_RING}`}
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
                                <Search className="h-4 w-4 shrink-0 text-ink-faint" />
                                <input
                                    ref={inputRef}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Nom, ville, SIRET…"
                                    className="flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
                                />
                                {searching && <span className="text-xs text-ink-faint">…</span>}
                            </div>
                            {results.length > 0 && (
                                <div className="mt-1.5 max-h-56 overflow-y-auto rounded-lg border border-border bg-surface shadow-float">
                                    {results.map((est) => (
                                        <button
                                            key={est._id}
                                            onClick={() => {
                                                setSelected(est);
                                                setQuery("");
                                                setResults([]);
                                            }}
                                            className="block w-full px-3 py-2 text-left transition-colors hover:bg-surface-sunken"
                                        >
                                            <p className="truncate text-sm text-ink">{est.name}</p>
                                            <p className="truncate text-xs text-ink-faint">
                                                {[est.address?.city, est.phone].filter(Boolean).join(" · ") || "—"}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* profile */}
                <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Profil correspondant</label>
                    <select
                        value={profileId}
                        onChange={(e) => setProfileId(e.target.value)}
                        className={`mt-1.5 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink ${FOCUS_RING}`}
                    >
                        {profiles.length === 0 && <option value="">Aucun profil</option>}
                        {profiles.map((p) => (
                            <option key={p._id} value={p._id}>
                                {p.name}
                                {p.active ? "" : " (inactif)"}
                            </option>
                        ))}
                    </select>
                    <p className="mt-1.5 text-xs text-ink-muted">
                        C'est ce nom qui s'affichera en étiquette sur la ligne.
                    </p>
                </div>
            </div>

            {error && <p className="mt-3 text-xs text-danger">{error}</p>}

            <div className="mt-4 flex items-center justify-end gap-2">
                <button
                    onClick={onClose}
                    className={`rounded-lg px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink ${FOCUS_RING}`}
                >
                    Annuler
                </button>
                <button
                    onClick={submit}
                    disabled={!selected || !profileId || saving}
                    className={`inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm font-medium text-ink-inverse transition-colors hover:bg-ink/90 disabled:pointer-events-none disabled:opacity-40 ${FOCUS_RING}`}
                >
                    <Plus className="h-4 w-4" />
                    {saving ? "Ajout…" : "Ajouter à la liste"}
                </button>
            </div>
        </div>
    );
}

// ─── working-list row ─────────────────────────────────────────────────────────

function CallRow({
    task,
    onChanged,
    onRemoved,
}: {
    task: CallTask;
    onChanged: (task: CallTask) => void;
    onRemoved: (id: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const [outcome, setOutcome] = useState<CallOutcome>("reached");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const left = daysLeft(task.expiresAt);

    const startClosing = () => {
        setOpen(true);
        setClosing(true);
    };

    const confirmCall = async () => {
        setBusy(true);
        try {
            onChanged(await markCalled(task._id, outcome, note.trim() || undefined));
            setNote("");
            setClosing(false);
        } finally {
            setBusy(false);
        }
    };

    const noAnswer = async () => {
        setBusy(true);
        try {
            onChanged(await logCallAttempt(task._id));
        } finally {
            setBusy(false);
        }
    };

    const saveNote = async () => {
        const text = note.trim();
        if (!text) return;
        setBusy(true);
        try {
            onChanged(await addCallNote(task._id, text));
            setNote("");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        try {
            await dropCallTask(task._id);
            onRemoved(task._id);
        } finally {
            setBusy(false);
        }
    };

    const city = [task.address?.city, task.address?.postcode].filter(Boolean).join(" · ");

    return (
        <>
            <tr className="border-t border-border transition-colors hover:bg-surface-sunken/40">
                <td className="px-6 py-4 align-top">
                    <div className="flex items-start gap-2">
                        <button
                            onClick={() => setOpen((o) => !o)}
                            title={open ? "Replier" : "Voir les notes"}
                            className={`mt-0.5 rounded p-0.5 text-ink-faint transition-colors hover:text-ink ${FOCUS_RING}`}
                        >
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                                <a
                                    href={`/establishments/${task.establishmentId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`truncate text-sm font-medium text-ink hover:text-accent ${FOCUS_RING}`}
                                >
                                    {task.establishmentName}
                                </a>
                                <ExternalLink className="h-3 w-3 shrink-0 text-ink-faint" />
                            </div>
                            {city && (
                                <p className="mt-0.5 flex items-center gap-1 text-xs text-ink-muted">
                                    <MapPin className="h-3 w-3 shrink-0" />
                                    {city}
                                </p>
                            )}
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                {task.source === "manual" && (
                                    <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs font-medium text-ink-muted">
                                        Ajout manuel
                                    </span>
                                )}
                                {task.attempts > 0 && (
                                    <span className="inline-flex items-center gap-1 rounded bg-warning-subtle px-1.5 py-0.5 text-xs font-medium text-warning">
                                        <PhoneOff className="h-3 w-3" />
                                        {task.attempts} essai{task.attempts > 1 ? "s" : ""} sans réponse
                                    </span>
                                )}
                                {task.notes.length > 0 && (
                                    <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs font-medium text-ink-muted">
                                        {task.notes.length} note{task.notes.length > 1 ? "s" : ""}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </td>

                <td className="px-6 py-4 align-top">
                    <ProfileTag name={task.profileName} />
                    {task.scheduledFor && (
                        <p className="mt-1.5 text-xs text-ink-faint">Envoi le {formatDate(task.scheduledFor)}</p>
                    )}
                </td>

                <td className="px-6 py-4 align-top">
                    <ContactCell task={task} />
                </td>

                <td className="px-6 py-4 align-top">
                    {task.owner ? (
                        <div className="flex items-start gap-1.5">
                            <User className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-faint" />
                            <div className="min-w-0">
                                <p className="truncate text-sm text-ink">{task.owner.name}</p>
                                <p className="text-xs text-ink-faint">
                                    {[
                                        task.owner.age ? `${task.owner.age} ans` : null,
                                        `${task.owner.establishmentCount} étab.`,
                                    ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <span className="text-xs text-ink-faint">—</span>
                    )}
                </td>

                <td className="px-6 py-4 text-right align-top">
                    <span
                        className={`font-mono text-sm tabular-nums ${left <= 1 ? "text-danger" : left <= 2 ? "text-warning" : "text-ink-muted"}`}
                        title={`Retiré automatiquement le ${formatDate(task.expiresAt)}`}
                    >
                        {left > 0 ? `J-${left}` : "Aujourd'hui"}
                    </span>
                </td>

                <td className="px-6 py-4 align-top">
                    <div className="flex items-center justify-end gap-1.5">
                        <button
                            onClick={noAnswer}
                            disabled={busy}
                            title="Personne n'a décroché — la ligne reste dans la liste"
                            className={`inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink disabled:opacity-40 ${FOCUS_RING}`}
                        >
                            <PhoneOff className="h-3.5 w-3.5" />
                            Pas de réponse
                        </button>
                        <button
                            onClick={startClosing}
                            disabled={busy}
                            title="L'appel a eu lieu — la ligne passe dans l'historique"
                            className={`inline-flex items-center gap-1.5 rounded-lg bg-ink px-2.5 py-1.5 text-xs font-medium text-ink-inverse transition-colors hover:bg-ink/90 disabled:opacity-40 ${FOCUS_RING}`}
                        >
                            <Check className="h-3.5 w-3.5" />
                            Appelé
                        </button>
                        <button
                            onClick={remove}
                            disabled={busy}
                            title="Retirer de la liste"
                            className={`rounded-lg p-1.5 text-ink-faint transition-colors hover:bg-danger-subtle hover:text-danger disabled:opacity-40 ${FOCUS_RING}`}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </td>
            </tr>

            {open && (
                <tr className="border-t border-border bg-canvas">
                    <td colSpan={6} className="px-6 py-5">
                        <div className="grid gap-6 md:grid-cols-2">
                            <div className="space-y-3">
                                {task.reason && (
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                            Pourquoi il est dans le brief
                                        </p>
                                        <p className="mt-1 text-sm text-ink-muted">{task.reason}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Notes</p>
                                    <div className="mt-2">
                                        <NoteList notes={task.notes} />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                        {closing ? "Note de l'appel" : "Ajouter une note"}
                                    </label>
                                    <textarea
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        rows={3}
                                        placeholder="Ce qui s'est dit, ce qu'il faut retenir…"
                                        className={`mt-1.5 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-faint ${FOCUS_RING}`}
                                    />
                                </div>

                                {closing ? (
                                    <>
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                                Résultat de l'appel
                                            </p>
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {OUTCOMES.map((value) => (
                                                    <button
                                                        key={value}
                                                        onClick={() => setOutcome(value)}
                                                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${FOCUS_RING} ${
                                                            outcome === value
                                                                ? OUTCOME_TONE[value]
                                                                : "border-border text-ink-muted hover:bg-surface-sunken"
                                                        }`}
                                                    >
                                                        {OUTCOME_LABEL[value]}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => setClosing(false)}
                                                className={`rounded-lg px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink ${FOCUS_RING}`}
                                            >
                                                Annuler
                                            </button>
                                            <button
                                                onClick={confirmCall}
                                                disabled={busy}
                                                className={`inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm font-medium text-ink-inverse transition-colors hover:bg-ink/90 disabled:opacity-40 ${FOCUS_RING}`}
                                            >
                                                <Check className="h-4 w-4" />
                                                Clôturer l'appel
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center justify-end">
                                        <button
                                            onClick={saveNote}
                                            disabled={busy || !note.trim()}
                                            className={`rounded-lg border border-border-strong bg-surface px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-sunken disabled:opacity-40 ${FOCUS_RING}`}
                                        >
                                            Enregistrer la note
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

// ─── history row ──────────────────────────────────────────────────────────────

function HistoryRow({ task, onChanged }: { task: CallTask; onChanged: (task: CallTask) => void }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const reopen = async () => {
        setBusy(true);
        try {
            onChanged(await reopenCallTask(task._id));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <tr className="border-t border-border transition-colors hover:bg-surface-sunken/40">
                <td className="px-6 py-3.5 align-top">
                    <div className="flex items-start gap-2">
                        <button
                            onClick={() => setOpen((o) => !o)}
                            className={`mt-0.5 rounded p-0.5 text-ink-faint transition-colors hover:text-ink ${FOCUS_RING}`}
                        >
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <div className="min-w-0">
                            <a
                                href={`/establishments/${task.establishmentId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`truncate text-sm text-ink hover:text-accent ${FOCUS_RING}`}
                            >
                                {task.establishmentName}
                            </a>
                            {task.address?.city && <p className="text-xs text-ink-faint">{task.address.city}</p>}
                        </div>
                    </div>
                </td>
                <td className="px-6 py-3.5 align-top">
                    <ProfileTag name={task.profileName} />
                </td>
                <td className="px-6 py-3.5 align-top">
                    {task.status === "called" ? (
                        <OutcomeBadge outcome={task.outcome} />
                    ) : (
                        <span className="text-xs text-ink-muted">
                            {task.dropReason ? DROP_REASON_LABEL[task.dropReason] : "Tombé de la liste"}
                        </span>
                    )}
                </td>
                <td className="px-6 py-3.5 text-right align-top font-mono text-sm tabular-nums text-ink-muted">
                    {task.notes.length}
                </td>
                <td className="px-6 py-3.5 align-top text-xs text-ink-muted">{task.calledBy ?? "—"}</td>
                <td className="px-6 py-3.5 align-top text-right text-xs text-ink-muted">
                    {formatDateTime(task.calledAt ?? task.droppedAt ?? task.updatedAt)}
                </td>
                <td className="px-6 py-3.5 align-top text-right">
                    <button
                        onClick={reopen}
                        disabled={busy}
                        title="Remettre dans la liste à appeler"
                        className={`rounded-lg p-1.5 text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink disabled:opacity-40 ${FOCUS_RING}`}
                    >
                        <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                </td>
            </tr>
            {open && (
                <tr className="border-t border-border bg-canvas">
                    <td colSpan={7} className="px-6 py-4">
                        <NoteList notes={task.notes} />
                    </td>
                </tr>
            )}
        </>
    );
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function CallList() {
    const [data, setData] = useState<CallListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [profileFilter, setProfileFilter] = useState("");
    const [search, setSearch] = useState("");
    const [historyPage, setHistoryPage] = useState(1);
    const [adding, setAdding] = useState(false);
    const [toast, setToast] = useState<string | null>(null);

    const showToast = (message: string) => {
        setToast(message);
        setTimeout(() => setToast(null), 2500);
    };

    const load = useCallback(async (profileId: string, q: string, page: number) => {
        setLoading(true);
        try {
            setData(await getCallList({ profileId: profileId || undefined, q: q || undefined, page }));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => load(profileFilter, search, historyPage), search ? 300 : 0);
        return () => clearTimeout(timer);
    }, [profileFilter, search, historyPage, load]);

    /**
     * One task changed. Rather than refetching the page, the row is moved between the two
     * lists locally — a caller who has just closed a call has to see it leave the list at
     * once, and a round-trip on every click makes the page feel like it is fighting back.
     */
    const applyChange = (task: CallTask) => {
        setData((prev) => {
            if (!prev) return prev;
            const previous = [...prev.todo, ...prev.history].find((t) => t._id === task._id);
            const todo = prev.todo.filter((t) => t._id !== task._id);
            const history = prev.history.filter((t) => t._id !== task._id);
            if (task.status === "todo") todo.unshift(task);
            else history.unshift(task);
            return { ...prev, todo, history, stats: shiftStats(prev.stats, previous?.status, task.status) };
        });
    };

    const removeTask = (id: string) => {
        setData((prev) => {
            if (!prev) return prev;
            const previous = prev.todo.find((t) => t._id === id);
            return {
                ...prev,
                todo: prev.todo.filter((t) => t._id !== id),
                stats: shiftStats(prev.stats, previous?.status, "dropped"),
            };
        });
        showToast("Retiré de la liste");
    };

    const profiles = data?.profiles ?? [];
    const todo = data?.todo ?? [];
    const history = data?.history ?? [];

    return (
        <div className="min-h-screen bg-canvas">
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-2xl bg-ink px-4 py-3 text-sm text-ink-inverse shadow-float">
                    <span>{toast}</span>
                    <button onClick={() => setToast(null)} className={`rounded ${FOCUS_RING}`}>
                        <X className="h-4 w-4 text-ink-inverse-muted hover:text-ink-inverse" />
                    </button>
                </div>
            )}

            <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
                <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <h1 className="font-display text-3xl font-normal tracking-tight text-ink">
                            Backoffice commercial
                        </h1>
                        <p className="mt-1 text-sm text-ink-muted">
                            Les établissements du prochain brief, à appeler avant que le client ne les lise.
                        </p>
                    </div>
                    <BOCommercialNav />
                </header>

                {/* ─── working list ─────────────────────────────────────────── */}
                <section className="overflow-hidden rounded-2xl border border-border bg-surface">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5">
                        <div>
                            <h2 className="text-base font-semibold text-ink">À appeler</h2>
                            <p className="mt-0.5 text-xs text-ink-muted">
                                {loading
                                    ? "Chargement…"
                                    : `${todo.length} établissement${todo.length > 1 ? "s" : ""} en attente d'appel`}
                            </p>
                        </div>
                        <span className="text-xs text-ink-faint">
                            listes générées {TTL_DAYS} jours avant l'envoi · remplacées à chaque nouveau brief
                        </span>
                    </div>

                    {/* toolbar */}
                    <div className="flex flex-wrap items-center gap-2 border-t border-border px-6 py-3">
                        <button
                            onClick={() => {
                                setProfileFilter("");
                                setHistoryPage(1);
                            }}
                            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${FOCUS_RING} ${
                                profileFilter === "" ? "bg-ink text-ink-inverse" : "text-ink-muted hover:bg-surface-sunken"
                            }`}
                        >
                            Tous les profils
                        </button>
                        {profiles.map((p) => (
                            <button
                                key={p._id}
                                onClick={() => {
                                    setProfileFilter(p._id);
                                    setHistoryPage(1);
                                }}
                                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${FOCUS_RING} ${
                                    profileFilter === p._id
                                        ? "bg-ink text-ink-inverse"
                                        : "text-ink-muted hover:bg-surface-sunken"
                                }`}
                            >
                                {p.name}
                            </button>
                        ))}

                        <div className="ml-auto flex items-center gap-2">
                            <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-1.5">
                                <Search className="h-3.5 w-3.5 text-ink-faint" />
                                <input
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setHistoryPage(1);
                                    }}
                                    placeholder="Nom, ville, dirigeant…"
                                    className="w-44 bg-transparent text-xs text-ink outline-none placeholder:text-ink-faint"
                                />
                            </div>
                            <button
                                onClick={() => setAdding((a) => !a)}
                                className={`inline-flex items-center gap-1.5 rounded-lg border border-border-strong bg-surface px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-surface-sunken ${FOCUS_RING}`}
                            >
                                <Plus className="h-3.5 w-3.5" />
                                Ajouter un appel
                            </button>
                        </div>
                    </div>

                    {adding && (
                        <AddCallPanel
                            profiles={profiles}
                            onAdded={(task) => {
                                applyChange(task);
                                showToast(`${task.establishmentName} ajouté à la liste`);
                            }}
                            onClose={() => setAdding(false)}
                        />
                    )}

                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[64rem] text-left">
                            <thead className="border-t border-border bg-surface-sunken text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                <tr>
                                    <th className="px-6 py-3 font-semibold">Établissement</th>
                                    <th className="px-6 py-3 font-semibold">Profil</th>
                                    <th className="px-6 py-3 font-semibold">Contact</th>
                                    <th className="px-6 py-3 font-semibold">Dirigeant</th>
                                    <th className="px-6 py-3 text-right font-semibold">Expire</th>
                                    <th className="px-6 py-3 text-right font-semibold">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <tr key={i} className="border-t border-border">
                                            <td colSpan={6} className="px-6 py-5">
                                                <div className="h-4 w-1/3 animate-pulse rounded-full bg-surface-sunken" />
                                            </td>
                                        </tr>
                                    ))
                                ) : todo.length === 0 ? (
                                    <tr className="border-t border-border">
                                        <td colSpan={6} className="px-6 py-16 text-center">
                                            <Building2 className="mx-auto h-6 w-6 text-ink-faint" />
                                            <p className="mt-3 text-sm text-ink-muted">
                                                Rien à appeler pour l'instant.
                                            </p>
                                            <p className="mt-1 text-xs text-ink-faint">
                                                La liste se remplit toute seule {TTL_DAYS} jours avant chaque envoi.
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    todo.map((task) => (
                                        <CallRow key={task._id} task={task} onChanged={applyChange} onRemoved={removeTask} />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-start gap-2 border-t border-border px-6 py-4">
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-faint" />
                        <p className="text-xs text-ink-muted">
                            Chaque brief hebdomadaire est préparé {TTL_DAYS} jours avant son envoi, et ses
                            établissements atterrissent ici le jour même. « Appelé » sort la ligne de la liste et la
                            classe en bas avec ses notes ; « Pas de réponse » la laisse en place avec un compteur
                            d'essais. À la génération suivante, tout ce qui reste ouvert sur ce profil est remplacé par
                            la nouvelle liste. Les ajouts manuels, eux, survivent à cette bascule et tombent sur leur
                            propre horloge, {TTL_DAYS} jours après avoir été saisis.
                        </p>
                    </div>
                </section>

                {/* ─── history ──────────────────────────────────────────────── */}
                <section className="mt-6 overflow-hidden rounded-2xl border border-border bg-surface">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5">
                        <div>
                            <h2 className="text-base font-semibold text-ink">Déjà traités</h2>
                            <p className="mt-0.5 text-xs text-ink-muted">
                                Les appels passés et les lignes tombées de la liste, avec leurs notes.
                            </p>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-ink-muted">
                            <span>
                                <span className="font-mono tabular-nums text-ink">{data?.stats.called ?? 0}</span> appelés
                            </span>
                            <span>
                                <span className="font-mono tabular-nums text-ink">{data?.stats.dropped ?? 0}</span> tombés
                            </span>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[56rem] text-left">
                            <thead className="border-t border-border bg-surface-sunken text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                <tr>
                                    <th className="px-6 py-3 font-semibold">Établissement</th>
                                    <th className="px-6 py-3 font-semibold">Profil</th>
                                    <th className="px-6 py-3 font-semibold">Résultat</th>
                                    <th className="px-6 py-3 text-right font-semibold">Notes</th>
                                    <th className="px-6 py-3 font-semibold">Par</th>
                                    <th className="px-6 py-3 text-right font-semibold">Quand</th>
                                    <th className="px-6 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {history.length === 0 && !loading ? (
                                    <tr className="border-t border-border">
                                        <td colSpan={7} className="px-6 py-12 text-center text-sm text-ink-muted">
                                            Aucun appel traité pour l'instant.
                                        </td>
                                    </tr>
                                ) : (
                                    history.map((task) => (
                                        <HistoryRow key={task._id} task={task} onChanged={applyChange} />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {(data?.historyPages ?? 1) > 1 && (
                        <div className="flex items-center justify-between border-t border-border px-6 py-4">
                            <button
                                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                                disabled={historyPage === 1}
                                className={`text-xs font-medium text-ink-muted transition-colors hover:text-ink disabled:opacity-30 ${FOCUS_RING}`}
                            >
                                ← Précédent
                            </button>
                            <span className="text-xs text-ink-faint">
                                Page {historyPage} / {data?.historyPages} · {data?.historyTotal} lignes
                            </span>
                            <button
                                onClick={() => setHistoryPage((p) => Math.min(data?.historyPages ?? 1, p + 1))}
                                disabled={historyPage === (data?.historyPages ?? 1)}
                                className={`text-xs font-medium text-ink-muted transition-colors hover:text-ink disabled:opacity-30 ${FOCUS_RING}`}
                            >
                                Suivant →
                            </button>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
